<script setup>
/* global axios */
// Disparador — lista de campanhas (massa de WhatsApp) + aba Blacklist.
// Fase 1: CRUD/lista/controle e blacklist. Wizard e envio em fases seguintes.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const tab = ref('campaigns');
const campaigns = ref([]);
const blacklist = ref([]);
const loading = ref(true);
const statusFilter = ref('');
const search = ref('');
const blForm = ref({ phones: '', reason: '' });

const STATUS = {
  draft: 'bg-n-alpha-2 text-n-slate-11',
  scheduled: 'bg-n-blue-3 text-n-blue-11',
  running: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-iris-3 text-n-iris-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};
const statusLabel = s => t(`DISPARADOR.status.${s}`);
const STATUS_OPTIONS = [
  'draft',
  'scheduled',
  'running',
  'paused',
  'completed',
  'cancelled',
];

const filtered = computed(() =>
  campaigns.value.filter(
    c =>
      (!statusFilter.value || c.status === statusFilter.value) &&
      (!search.value ||
        c.name.toLowerCase().includes(search.value.toLowerCase()))
  )
);
const pct = c =>
  c.total_contacts
    ? Math.round(((c.sent_count + c.failed_count) / c.total_contacts) * 100)
    : 0;
const fmtDate = iso => (iso ? new Date(iso).toLocaleString() : '—');

const loadCampaigns = async () => {
  loading.value = true;
  try {
    campaigns.value = (await axios.get(`${base}/campaigns`)).data.data || [];
  } finally {
    loading.value = false;
  }
};
const loadBlacklist = async () => {
  blacklist.value =
    (await axios.get(`${base}/campaign_blacklists`)).data.data || [];
};

const act = async (c, action) => {
  await axios.post(`${base}/campaigns/${c.id}/${action}`);
  await loadCampaigns();
};
const remove = async c => {
  if (!window.confirm(t('DISPARADOR.confirmDelete', { name: c.name }))) return;
  await axios.delete(`${base}/campaigns/${c.id}`);
  await loadCampaigns();
};
const openReport = c =>
  router.push({
    name: 'kanban_campaign_detail',
    params: { accountId, campaignId: c.id },
  });

const addBlacklist = async () => {
  if (!blForm.value.phones.trim()) return;
  await axios.post(`${base}/campaign_blacklists`, blForm.value);
  blForm.value = { phones: '', reason: '' };
  await loadBlacklist();
};
const removeBlacklist = async b => {
  await axios.delete(`${base}/campaign_blacklists/${b.id}`);
  await loadBlacklist();
};
// download via blob (GET puro cairia em authenticate_user! 401 — auth é via token no header)
const exportBlacklist = async () => {
  const res = await axios.get(`${base}/campaign_blacklists/export`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'blacklist.csv';
  a.click();
  URL.revokeObjectURL(url);
};
// --- Sanitização ---
const sanInstances = ref([]);
const sanJobs = ref([]);
const sanForm = ref({ instance_name: '', file: null });
const sanFileName = ref('');
const sanUploading = ref(false);
let sanTimer = null;

const refreshSanJobs = async () => {
  sanJobs.value = (await axios.get(`${base}/sanitize_jobs`)).data.data || [];
  const busy = sanJobs.value.some(j =>
    ['pending', 'processing'].includes(j.status)
  );
  if (!busy && sanTimer) {
    clearInterval(sanTimer);
    sanTimer = null;
  } else if (busy && !sanTimer) {
    sanTimer = setInterval(refreshSanJobs, 4000);
  }
};
const loadSanitize = async () => {
  sanInstances.value =
    (await axios.get(`${base}/sanitize_jobs/instances`)).data.data || [];
  if (sanInstances.value.length && !sanForm.value.instance_name)
    sanForm.value.instance_name = sanInstances.value[0].instance_name;
  await refreshSanJobs();
};
const onSanFile = e => {
  sanForm.value.file = e.target.files?.[0] || null;
  sanFileName.value = sanForm.value.file?.name || '';
};
const uploadSanitize = async () => {
  if (!sanForm.value.file || !sanForm.value.instance_name) return;
  sanUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', sanForm.value.file);
    fd.append('instance_name', sanForm.value.instance_name);
    await axios.post(`${base}/sanitize_jobs`, fd);
    sanForm.value.file = null;
    sanFileName.value = '';
    await loadSanitize();
  } catch (e) {
    window.alert(
      e?.response?.data?.error || t('DISPARADOR.sanitize.uploadError')
    );
  } finally {
    sanUploading.value = false;
  }
};
const downloadSan = async (job, filter) => {
  const res = await axios.get(`${base}/sanitize_jobs/${job.id}/download`, {
    params: { filter },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sanitizacao-${job.id}-${filter}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
const removeSan = async job => {
  if (!window.confirm(t('DISPARADOR.sanitize.confirmDelete'))) return;
  await axios.delete(`${base}/sanitize_jobs/${job.id}`);
  await loadSanitize();
};
const sanProgress = j =>
  j.total ? Math.round((j.checked_count / j.total) * 100) : 0;
const sanStatusLabel = s => t(`DISPARADOR.sanitize.status.${s}`, s);

// --- Ligações em massa (voice blast) ---
const callInstances = ref([]);
const callBroadcasts = ref([]);
const callForm = ref({
  instance_name: '',
  file: null,
  audio: null,
  concurrency: 1,
  gap_ms: 2000,
  max_ring_ms: 30000,
});
const callFileName = ref('');
const callAudioName = ref('');
const callUploading = ref(false);
let callTimer = null;

// atualiza os que estão rodando — o `show` sincroniza o status do AstraCall
const syncRunningBroadcasts = async () => {
  const running = callBroadcasts.value.filter(b => b.status === 'running');
  await Promise.all(
    running.map(async b => {
      try {
        const { data } = await axios.get(`${base}/call_broadcasts/${b.id}`);
        const i = callBroadcasts.value.findIndex(x => x.id === b.id);
        if (i >= 0 && data.data) callBroadcasts.value[i] = data.data;
      } catch (e) {
        /* mantém o atual */
      }
    })
  );
};
const refreshCallBroadcasts = async () => {
  callBroadcasts.value =
    (await axios.get(`${base}/call_broadcasts`)).data.data || [];
  await syncRunningBroadcasts();
  const busy = callBroadcasts.value.some(b => b.status === 'running');
  if (!busy && callTimer) {
    clearInterval(callTimer);
    callTimer = null;
  } else if (busy && !callTimer) {
    callTimer = setInterval(refreshCallBroadcasts, 4000);
  }
};
const loadCalls = async () => {
  callInstances.value =
    (await axios.get(`${base}/call_broadcasts/instances`)).data.data || [];
  if (callInstances.value.length && !callForm.value.instance_name)
    callForm.value.instance_name = callInstances.value[0].instance_name;
  await refreshCallBroadcasts();
};
const onCallListFile = e => {
  callForm.value.file = e.target.files?.[0] || null;
  callFileName.value = callForm.value.file?.name || '';
};
const onCallAudioFile = e => {
  callForm.value.audio = e.target.files?.[0] || null;
  callAudioName.value = callForm.value.audio?.name || '';
};
const startCallBroadcast = async () => {
  const f = callForm.value;
  if (!f.file || !f.audio || !f.instance_name) return;
  callUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', f.file);
    fd.append('audio', f.audio);
    fd.append('instance_name', f.instance_name);
    fd.append('concurrency', f.concurrency);
    fd.append('gap_ms', f.gap_ms);
    fd.append('max_ring_ms', f.max_ring_ms);
    await axios.post(`${base}/call_broadcasts`, fd);
    f.file = null;
    f.audio = null;
    callFileName.value = '';
    callAudioName.value = '';
    await loadCalls();
  } catch (e) {
    window.alert(e?.response?.data?.error || t('DISPARADOR.calls.uploadError'));
  } finally {
    callUploading.value = false;
  }
};
const removeCallBroadcast = async b => {
  if (!window.confirm(t('DISPARADOR.calls.confirmDelete'))) return;
  await axios.delete(`${base}/call_broadcasts/${b.id}`);
  await loadCalls();
};
const callDone = b =>
  (b.answered_count || 0) + (b.no_answer_count || 0) + (b.failed_count || 0);
const callProgress = b => (b.total ? Math.round((callDone(b) / b.total) * 100) : 0);
const callStatusLabel = s => t(`DISPARADOR.calls.status.${s}`, s);

const switchTab = key => {
  tab.value = key;
  if (key === 'blacklist') loadBlacklist();
  if (key === 'sanitize') loadSanitize();
  if (key === 'calls') loadCalls();
};

onMounted(loadCampaigns);
onBeforeUnmount(() => {
  if (sanTimer) clearInterval(sanTimer);
  if (callTimer) clearInterval(callTimer);
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header class="px-6 py-4 border-b border-n-weak bg-n-solid-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-brand"
          >
            <svg
              class="w-4 h-4 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
              />
            </svg>
          </div>
          <h1 class="text-heading-1 text-n-slate-12">
            {{ t('DISPARADOR.title') }}
          </h1>
        </div>
        <div v-if="tab !== 'blacklist'" class="flex gap-2">
          <button
            class="inline-flex items-center h-8 gap-2 px-3 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            @click="switchTab('blacklist')"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
            {{ t('DISPARADOR.tabs.blacklist') }}
          </button>
          <button
            class="inline-flex items-center h-8 gap-2 px-3 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110"
            @click="
              router.push({
                name: 'kanban_campaign_wizard',
                params: { accountId },
              })
            "
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {{ t('DISPARADOR.newCampaign') }}
          </button>
        </div>
        <button
          v-else
          class="inline-flex items-center h-8 gap-2 px-3 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
          @click="switchTab('campaigns')"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {{ t('DISPARADOR.tabs.campaigns') }}
        </button>
      </div>

      <div v-if="tab !== 'blacklist'" class="flex gap-1 mt-3">
        <button
          v-for="tb in ['campaigns', 'sanitize', 'calls']"
          :key="tb"
          class="px-4 py-1.5 text-sm font-medium rounded-lg transition-colors"
          :class="
            tab === tb
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="switchTab(tb)"
        >
          {{ t(`DISPARADOR.tabs.${tb}`) }}
        </button>
      </div>
    </header>

    <div
      v-if="tab === 'campaigns'"
      class="flex items-center justify-between px-6 py-3 border-b border-n-weak bg-n-solid-2"
    >
      <input
        v-model="search"
        class="px-3 transition-all border-0 rounded-lg outline outline-1 outline-offset-[-1px] w-56 h-7 text-sm font-normal bg-n-alpha-black2 placeholder:text-n-slate-10 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:ring-0"
        :placeholder="t('DISPARADOR.searchPlaceholder')"
      />
      <select
        v-model="statusFilter"
        class="!w-auto pl-3 pr-9 transition-all border-0 rounded-lg outline outline-1 outline-offset-[-1px] h-7 text-xs font-normal bg-n-alpha-black2 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:ring-0"
      >
        <option value="">{{ t('DISPARADOR.allStatuses') }}</option>
        <option v-for="s in STATUS_OPTIONS" :key="s" :value="s">
          {{ statusLabel(s) }}
        </option>
      </select>
    </div>

    <div class="flex-1 p-6 overflow-y-auto">
      <!-- aba campanhas -->
      <template v-if="tab === 'campaigns'">
        <div v-if="loading" class="text-sm text-n-slate-10">
          {{ t('DISPARADOR.loading') }}
        </div>
        <p
          v-else-if="!filtered.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('DISPARADOR.empty') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10 border-b border-n-weak">
              <th class="px-3 py-2 font-medium">
                {{ t('DISPARADOR.col.name') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('DISPARADOR.col.status') }}
              </th>
              <th class="px-3 py-2 font-medium w-56">
                {{ t('DISPARADOR.col.progress') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('DISPARADOR.col.createdAt') }}
              </th>
              <th class="px-3 py-2 font-medium text-right">
                {{ t('DISPARADOR.col.actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in filtered"
              :key="c.id"
              class="border-b border-n-weak hover:bg-n-alpha-1"
            >
              <td
                class="px-3 py-2.5 font-medium text-n-slate-12 cursor-pointer"
                @click="openReport(c)"
              >
                {{ c.name }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                  :class="STATUS[c.status]"
                  >{{ statusLabel(c.status) }}</span
                >
              </td>
              <td class="px-3 py-2.5">
                <div class="flex items-center gap-2 mb-0.5">
                  <div
                    class="flex-1 h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
                  >
                    <div
                      class="h-full bg-n-teal-9"
                      :style="{ width: `${pct(c)}%` }"
                    />
                  </div>
                  <span class="text-[0.6875rem] text-n-slate-10">{{
                    `${pct(c)}%`
                  }}</span>
                </div>
                <span class="text-[0.625rem] text-n-slate-10">{{
                  `${c.total_contacts} ${t('DISPARADOR.col.contacts')} · ${c.failed_count} ${t('DISPARADOR.col.failures')}`
                }}</span>
              </td>
              <td class="px-3 py-2.5 text-n-slate-10">
                {{ fmtDate(c.created_at) }}
              </td>
              <td class="px-3 py-2.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    v-if="['draft', 'scheduled', 'paused'].includes(c.status)"
                    class="p-1.5 rounded-md text-n-teal-11 hover:bg-n-alpha-1"
                    :title="t('DISPARADOR.act.start')"
                    @click="act(c, 'start')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                  <button
                    v-if="c.status === 'running'"
                    class="p-1.5 rounded-md text-n-amber-11 hover:bg-n-alpha-1"
                    :title="t('DISPARADOR.act.pause')"
                    @click="act(c, 'pause')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                    </svg>
                  </button>
                  <button
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
                    :title="t('DISPARADOR.act.report')"
                    @click="openReport(c)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 19v-6m4 6V5m4 14v-9M5 19h14"
                      />
                    </svg>
                  </button>
                  <button
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
                    :title="t('DISPARADOR.act.duplicate')"
                    @click="act(c, 'duplicate')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 8V6a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-2M6 20h8a2 2 0 002-2v-8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    v-if="!['running'].includes(c.status)"
                    class="p-1.5 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                    :title="t('DISPARADOR.act.delete')"
                    @click="remove(c)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- sanitizar lista (validação de números via instância conectada) -->
      <div v-else-if="tab === 'sanitize'" class="max-w-3xl">
        <div
          v-if="!sanInstances.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('DISPARADOR.sanitize.noInstances') }}
        </div>
        <template v-else>
          <!-- upload -->
          <div class="p-4 mb-6 border rounded-xl border-n-weak bg-n-solid-1">
            <p class="mb-3 text-sm text-n-slate-11">
              {{ t('DISPARADOR.sanitize.hint') }}
            </p>
            <div class="flex flex-wrap items-end gap-3">
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('DISPARADOR.sanitize.instance')
                }}</label>
                <select
                  v-model="sanForm.instance_name"
                  class="pl-3 pr-9 h-9 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
                >
                  <option
                    v-for="i in sanInstances"
                    :key="i.instance_name"
                    :value="i.instance_name"
                  >
                    {{ i.instance_name }}
                    {{ i.provider_name ? `(${i.provider_name})` : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('DISPARADOR.sanitize.file')
                }}</label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  class="text-sm text-n-slate-11 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-1.5 file:text-sm file:text-n-slate-12"
                  @change="onSanFile"
                />
              </div>
              <button
                class="inline-flex items-center h-9 gap-2 px-4 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-60"
                :disabled="sanUploading || !sanForm.file"
                @click="uploadSanitize"
              >
                {{
                  sanUploading
                    ? t('DISPARADOR.sanitize.uploading')
                    : t('DISPARADOR.sanitize.start')
                }}
              </button>
            </div>
          </div>

          <!-- jobs -->
          <p
            v-if="!sanJobs.length"
            class="py-8 text-sm text-center text-n-slate-10"
          >
            {{ t('DISPARADOR.sanitize.noJobs') }}
          </p>
          <div v-else class="flex flex-col gap-3">
            <div
              v-for="job in sanJobs"
              :key="job.id"
              class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div class="font-medium truncate text-n-slate-12">
                    {{ job.filename || `#${job.id}` }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ job.instance_name }} · {{ job.total }}
                    {{ t('DISPARADOR.sanitize.numbers') }}
                  </div>
                </div>
                <span
                  class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >{{ sanStatusLabel(job.status) }}</span
                >
              </div>

              <div
                v-if="['pending', 'processing'].includes(job.status)"
                class="mt-3"
              >
                <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
                  <div
                    class="h-full rounded-full bg-n-brand transition-all"
                    :style="{ width: `${sanProgress(job)}%` }"
                  />
                </div>
                <div class="mt-1 text-xs text-n-slate-10">
                  {{ job.checked_count }}/{{ job.total }}
                </div>
              </div>

              <div
                v-else-if="job.status === 'completed'"
                class="flex flex-wrap items-center gap-3 mt-3"
              >
                <span class="text-xs text-n-teal-11"
                  >{{ '✓' }} {{ job.valid_count }}
                  {{ t('DISPARADOR.sanitize.valid') }}</span
                >
                <span class="text-xs text-n-ruby-11"
                  >{{ '✕' }} {{ job.invalid_count }}
                  {{ t('DISPARADOR.sanitize.invalid') }}</span
                >
                <div class="flex items-center gap-2 ml-auto">
                  <button
                    class="px-2.5 py-1 text-xs font-medium rounded-md outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
                    @click="downloadSan(job, 'valid')"
                  >
                    {{ t('DISPARADOR.sanitize.dlValid') }}
                  </button>
                  <button
                    class="px-2.5 py-1 text-xs font-medium rounded-md outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
                    @click="downloadSan(job, 'all')"
                  >
                    {{ t('DISPARADOR.sanitize.dlAll') }}
                  </button>
                </div>
              </div>

              <p
                v-else-if="job.status === 'failed'"
                class="mt-2 text-xs text-n-ruby-11"
              >
                {{ job.error_message || t('DISPARADOR.sanitize.failed') }}
              </p>

              <div class="flex justify-end mt-2">
                <button
                  class="text-xs text-n-ruby-11 hover:underline"
                  @click="removeSan(job)"
                >
                  {{ t('DISPARADOR.sanitize.delete') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- aba Ligações em massa (voice blast) -->
      <div v-else-if="tab === 'calls'" class="max-w-3xl">
        <div
          v-if="!callInstances.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('DISPARADOR.calls.noInstances') }}
        </div>
        <template v-else>
          <!-- upload -->
          <div class="p-4 mb-6 border rounded-xl border-n-weak bg-n-solid-1">
            <p class="mb-3 text-sm text-n-slate-11">
              {{ t('DISPARADOR.calls.hint') }}
            </p>
            <div class="flex flex-wrap items-end gap-3">
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('DISPARADOR.calls.instance')
                }}</label>
                <select
                  v-model="callForm.instance_name"
                  class="pl-3 pr-9 h-9 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
                >
                  <option
                    v-for="i in callInstances"
                    :key="i.instance_name"
                    :value="i.instance_name"
                  >
                    {{ i.instance_name }}
                    {{ i.provider_name ? `(${i.provider_name})` : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('DISPARADOR.calls.file')
                }}</label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  class="text-sm text-n-slate-11 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-1.5 file:text-sm file:text-n-slate-12"
                  @change="onCallListFile"
                />
              </div>
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('DISPARADOR.calls.audio')
                }}</label>
                <input
                  type="file"
                  accept="audio/*"
                  class="text-sm text-n-slate-11 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-1.5 file:text-sm file:text-n-slate-12"
                  @change="onCallAudioFile"
                />
              </div>
            </div>
            <div class="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">{{
                  t('DISPARADOR.calls.concurrency')
                }}</label>
                <input
                  v-model.number="callForm.concurrency"
                  type="number"
                  min="1"
                  max="5"
                  class="w-20 !h-9 !mb-0"
                />
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">{{
                  t('DISPARADOR.calls.gap')
                }}</label>
                <input
                  v-model.number="callForm.gap_ms"
                  type="number"
                  min="0"
                  step="500"
                  class="w-28 !h-9 !mb-0"
                />
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">{{
                  t('DISPARADOR.calls.maxRing')
                }}</label>
                <input
                  v-model.number="callForm.max_ring_ms"
                  type="number"
                  min="5000"
                  step="1000"
                  class="w-28 !h-9 !mb-0"
                />
              </div>
              <button
                class="inline-flex items-center h-9 gap-2 px-4 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-60"
                :disabled="callUploading || !callForm.file || !callForm.audio"
                @click="startCallBroadcast"
              >
                {{
                  callUploading
                    ? t('DISPARADOR.calls.starting')
                    : t('DISPARADOR.calls.start')
                }}
              </button>
            </div>
          </div>

          <!-- campanhas de ligação -->
          <p
            v-if="!callBroadcasts.length"
            class="py-8 text-sm text-center text-n-slate-10"
          >
            {{ t('DISPARADOR.calls.noJobs') }}
          </p>
          <div v-else class="flex flex-col gap-3">
            <div
              v-for="bc in callBroadcasts"
              :key="bc.id"
              class="p-4 border rounded-xl border-n-weak bg-n-solid-1"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div class="font-medium truncate text-n-slate-12">
                    {{ bc.filename || `#${bc.id}` }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ bc.instance_name }} · {{ bc.total }}
                    {{ t('DISPARADOR.calls.numbers') }}
                  </div>
                </div>
                <span
                  class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >{{ callStatusLabel(bc.status) }}</span
                >
              </div>

              <div v-if="bc.status === 'running'" class="mt-3">
                <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
                  <div
                    class="h-full rounded-full bg-n-brand transition-all"
                    :style="{ width: `${callProgress(bc)}%` }"
                  />
                </div>
                <div class="mt-1 text-xs text-n-slate-10">
                  {{ callDone(bc) }}/{{ bc.total }}
                </div>
              </div>

              <div
                v-if="['running', 'done'].includes(bc.status)"
                class="flex flex-wrap items-center gap-3 mt-3"
              >
                <span class="text-xs text-n-teal-11"
                  >✓ {{ bc.answered_count }}
                  {{ t('DISPARADOR.calls.answered') }}</span
                >
                <span class="text-xs text-n-slate-11"
                  >☎ {{ bc.no_answer_count }}
                  {{ t('DISPARADOR.calls.noAnswer') }}</span
                >
                <span class="text-xs text-n-ruby-11"
                  >✕ {{ bc.failed_count }}
                  {{ t('DISPARADOR.calls.failed') }}</span
                >
              </div>

              <p
                v-else-if="bc.status === 'failed'"
                class="mt-2 text-xs text-n-ruby-11"
              >
                {{ bc.error_message || t('DISPARADOR.calls.failedMsg') }}
              </p>

              <div class="flex justify-end mt-2">
                <button
                  class="text-xs text-n-ruby-11 hover:underline"
                  @click="removeCallBroadcast(bc)"
                >
                  {{ t('DISPARADOR.calls.delete') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- aba blacklist -->
      <template v-else>
        <div class="flex flex-col max-w-2xl gap-3">
          <div class="flex gap-2">
            <input
              v-model="blForm.phones"
              class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('DISPARADOR.blacklist.phonesPlaceholder')"
            />
            <input
              v-model="blForm.reason"
              class="w-40 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('DISPARADOR.blacklist.reasonPlaceholder')"
            />
            <button
              class="px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
              :disabled="!blForm.phones.trim()"
              @click="addBlacklist"
            >
              {{ t('DISPARADOR.blacklist.add') }}
            </button>
            <button
              v-if="blacklist.length"
              class="px-3.5 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1"
              @click="exportBlacklist"
            >
              {{ t('DISPARADOR.blacklist.export') }}
            </button>
          </div>
          <p
            v-if="!blacklist.length"
            class="py-6 text-sm text-center text-n-slate-10"
          >
            {{ t('DISPARADOR.blacklist.empty') }}
          </p>
          <div
            v-for="b in blacklist"
            :key="b.id"
            class="flex items-center gap-2 px-3 py-2 border rounded-lg border-n-weak"
          >
            <span class="font-medium text-n-slate-12">{{ b.phone }}</span>
            <template v-if="b.reason">
              <span class="text-xs text-n-slate-10">— {{ b.reason }}</span>
            </template>
            <button
              class="ml-auto p-1 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
              :title="t('DISPARADOR.act.delete')"
              @click="removeBlacklist(b)"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
